/**
 * Data Service
 * @description 데이터 로딩 서비스
 */

import { httpClient, apiClient } from '@/utils/httpClient';

export interface Incident {
  id: string;
  number: string;
  opened: string;
  short_description: string;
  description: string;
  caller: string;
  reportedBy: string;
  accountId: string;
  accountName: string;
  priority: {
    level: number;
    label: string;
  };
  state: string;
  category: string;
  subcategory: string;
  assignment_group: string;
  assigned_to: string;
  affectedServices: string[];
  impactedUsers: string;
}

export interface ServiceRequest {
  id: string;
  number: string;
  opened: string;
  short_description: string;
  description: string;
  caller: string;
  priority: {
    level: number;
    label: string;
  };
  state: string;
  category: string;
  assignment_group: string;
  assigned_to: string;
}

export interface ChangeRequest {
  id: string;
  number: string;
  opened: string;
  short_description: string;
  state: string;
  priority: {
    level: number;
    label: string;
  };
}

class DataService {
  /**
   * Load onpremise service requests
   */
  async getOnpremiseOpenServiceRequests(): Promise<{
    requests: ServiceRequest[];
  }> {
    return httpClient.get<{ requests: ServiceRequest[] }>(
      '/onpremise/servicerequest/open-servicerequests.json',
      { skipAuth: true }
    );
  }

  /**
   * Load onpremise closed service requests
   */
  async getOnpremiseClosedServiceRequests(): Promise<{
    requests: ServiceRequest[];
  }> {
    return httpClient.get<{ requests: ServiceRequest[] }>(
      '/onpremise/servicerequest/closed-servicerequests.json',
      { skipAuth: true }
    );
  }

  /**
   * Load office service requests
   */
  async getOfficeOpenServiceRequests(): Promise<{
    requests: ServiceRequest[];
  }> {
    return httpClient.get<{ requests: ServiceRequest[] }>(
      '/office/servicerequest/open-servicerequests.json',
      { skipAuth: true }
    );
  }

  /**
   * Load retail store service requests
   */
  async getRetailStoreOpenServiceRequests(): Promise<{
    requests: ServiceRequest[];
  }> {
    return httpClient.get<{ requests: ServiceRequest[] }>(
      '/retailstore/servicerequest/open-servicerequests.json',
      { skipAuth: true }
    );
  }

  /**
   * Load onpremise inventory (agents)
   */
  async getOnpremiseInventoryAgents(): Promise<any> {
    return httpClient.get<any>(
      '/onpremise/inventory/inventory-agents.json',
      { skipAuth: true }
    );
  }

  /**
   * Load onpremise open change requests
   */
  async getOnpremiseOpenChangeRequests(): Promise<{
    changerequests: ChangeRequest[];
  }> {
    return httpClient.get<{ changerequests: ChangeRequest[] }>(
      '/onpremise/changes/open-changerequests.json',
      { skipAuth: true }
    );
  }

  /**
   * Load onpremise closed change requests
   */
  async getOnpremiseClosedChangeRequests(): Promise<{
    changerequests: ChangeRequest[];
  }> {
    return httpClient.get<{ changerequests: ChangeRequest[] }>(
      '/onpremise/changes/closed-changerequests.json',
      { skipAuth: true }
    );
  }

  /**
   * Load onpremise open incidents
   */
  async getOnpremiseOpenIncidents(): Promise<{
    incidents: Incident[];
  }> {
    return httpClient.get<{ incidents: Incident[] }>(
      '/onpremise/incidents/open-incidents.json',
      { skipAuth: true }
    );
  }

  /**
   * Load onpremise closed incidents
   */
  async getOnpremiseClosedIncidents(): Promise<{
    incidents: Incident[];
  }> {
    return httpClient.get<{ incidents: Incident[] }>(
      '/onpremise/incidents/closed-incidents.json',
      { skipAuth: true }
    );
  }

  /**
   * Load onpremise security findings
   */
  async getOnpremiseSecurityFindings(): Promise<any> {
    return httpClient.get<any>(
      '/onpremise/collectors/security-findings.json',
      { skipAuth: true }
    );
  }

  /**
   * Load tenant access control data
   */
  async getTenantAccessControl(tenantId: string): Promise<any> {
    return apiClient.get<any>(`/tenants/${tenantId}/access-control`);
  }

  /**
   * Load tenant organization data
   */
  async getTenantOrganization(tenantId: string): Promise<any> {
    return apiClient.get<any>(`/tenants/${tenantId}/organization`);
  }

  async getTenantMenuConfig(tenantId: string): Promise<any> {
    return apiClient.get<any>(`/tenants/${tenantId}/menu-config`);
  }

  async getTenantMenuCatalog(tenantId: string): Promise<any> {
    return apiClient.get<any>(`/tenants/${tenantId}/menu-config/catalog`);
  }

  async updateTenantMenuConfig(tenantId: string, data: any): Promise<any> {
    return apiClient.put<any>(`/tenants/${tenantId}/menu-config`, data);
  }

  /**
   * Load tenant overview data
   */
  async getTenantOverview(tenantId: string): Promise<any> {
    return apiClient.get<any>(`/tenants/${tenantId}/overview`);
  }

  /**
   * Update tenant organization data
   */
  async updateTenantOrganization(tenantId: string, data: any): Promise<any> {
    return apiClient.put<any>(`/tenants/${tenantId}/organization`, data);
  }

  /**
   * Update tenant access control data
   */
  async updateTenantAccessControl(tenantId: string, data: any): Promise<any> {
    return apiClient.put<any>(`/tenants/${tenantId}/access-control`, data);
  }

  /**
   * Load tenant credentials (user authentication data)
   */
  async getTenantCredentials(tenantId: string): Promise<any> {
    return apiClient.get<any>(`/tenants/${tenantId}/credentials`);
  }

  /**
   * Update tenant credentials (user authentication data)
   */
  async updateTenantCredentials(tenantId: string, data: any): Promise<any> {
    return apiClient.put<any>(`/tenants/${tenantId}/credentials`, data);
  }

  /**
   * Load tenant connectors
   */
  async getTenantConnectors(tenantId: string): Promise<any> {
    return apiClient.get<any>(`/tenants/${tenantId}/integrations/connectors`);
  }

  /**
   * Update tenant connectors
   */
  async updateTenantConnectors(tenantId: string, data: any): Promise<any> {
    return apiClient.put<any>(`/tenants/${tenantId}/integrations/connectors`, data);
  }
}

export const dataService = new DataService();

